import React from "react";
import { Button, Row, Col, Typography, Spin, Space, Tag } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
  ForwardOutlined,
} from "@ant-design/icons";
import { useTranslate } from "@pankod/refine-core";
import { IQuiz } from "interfaces";

const { Title, Paragraph, Text } = Typography;

interface ReviewQuizCardProps {
  quiz: IQuiz | null;
  loading: boolean;
  selectedAnswer: number | null;
  onSelectAnswer: (n: number) => void;
  onApprove: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onSkip: () => void;
  actionLoading: "approve" | "delete" | null;
}

const ReviewQuizCard: React.FC<ReviewQuizCardProps> = ({
  quiz,
  loading,
  selectedAnswer,
  onSelectAnswer,
  onApprove,
  onEdit,
  onDelete,
  onSkip,
  actionLoading,
}) => {
  const t = useTranslate();

  if (loading || !quiz) {
    return (
      <div style={{ textAlign: "center", padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  const correct = Number(quiz.correct_answer);
  const revealed = selectedAnswer !== null;
  const answers = [quiz.answer1, quiz.answer2, quiz.answer3, quiz.answer4];

  const buttonStyleFor = (idx: number): React.CSSProperties => {
    const n = idx + 1;
    if (!revealed) return {};
    if (n === correct) {
      return {
        backgroundColor: "#f6ffed",
        borderColor: "#52c41a",
        color: "#389e0d",
      };
    }
    if (n === selectedAnswer && n !== correct) {
      return {
        backgroundColor: "#fff1f0",
        borderColor: "#ff4d4f",
        color: "#cf1322",
      };
    }
    return { opacity: 0.6 };
  };

  return (
    <div>
      <Title level={4} style={{ marginTop: 0 }}>
        {quiz.question}
      </Title>

      <Row gutter={[12, 12]} style={{ marginTop: 24, marginBottom: 24 }}>
        {answers.map((ans, idx) => {
          const n = idx + 1;
          return (
            <Col span={12} key={idx}>
              <Button
                block
                size="large"
                onClick={() => !revealed && onSelectAnswer(n)}
                disabled={revealed}
                style={{
                  height: "auto",
                  whiteSpace: "normal",
                  textAlign: "left",
                  padding: "12px 16px",
                  ...buttonStyleFor(idx),
                }}
                icon={
                  revealed && n === correct ? (
                    <CheckCircleOutlined />
                  ) : revealed && n === selectedAnswer ? (
                    <CloseCircleOutlined />
                  ) : (
                    <Tag style={{ marginRight: 4 }}>{n}</Tag>
                  )
                }
              >
                {ans}
              </Button>
            </Col>
          );
        })}
      </Row>

      {revealed && (
        <div
          style={{
            backgroundColor: "#fafafa",
            padding: 12,
            borderRadius: 4,
            marginBottom: 16,
          }}
        >
          <Text strong>{t("quiz.fields.explanation") || "Explanation"}: </Text>
          <Paragraph style={{ marginBottom: 0, marginTop: 4 }}>
            {quiz.explanation || "—"}
          </Paragraph>
        </div>
      )}

      {revealed && (
        <Space style={{ marginTop: 8 }} wrap>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={onApprove}
            loading={actionLoading === "approve"}
            disabled={actionLoading !== null}
          >
            {t("quiz-ai-creation.review.approve_translate")}
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={onEdit}
            disabled={actionLoading !== null}
          >
            {t("quiz-ai-creation.review.edit")}
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={onDelete}
            loading={actionLoading === "delete"}
            disabled={actionLoading !== null}
          >
            {t("quiz-ai-creation.review.delete")}
          </Button>
          <Button
            type="link"
            icon={<ForwardOutlined />}
            onClick={onSkip}
            disabled={actionLoading !== null}
          >
            {t("quiz-ai-creation.review.skip")}
          </Button>
        </Space>
      )}
    </div>
  );
};

export default ReviewQuizCard;
